import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import "./Notifications.css";

const NotificationDropdown = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await API.get("/notifications/unread_count/");
            setUnreadCount(response.data.count);
        } catch (err) {
            console.error("Error fetching unread count:", err);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get("/notifications/?limit=10");
            setNotifications(response.data.slice(0, 10));
        } catch (err) {
            console.error("Error fetching notifications:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();
        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, fetchNotifications]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await API.post(`/notifications/${notificationId}/mark_as_read/`);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error("Error marking notification as read:", err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await API.post("/notifications/mark_read/", {});
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error("Error marking all as read:", err);
        }
    };

    const handleNotificationClick = (notification) => {
        // Mark as read
        if (!notification.read) {
            handleMarkAsRead(notification.id);
        }

        // Navigate based on notification type
        if (notification.project_id) {
            navigate(`/Projects/${notification.project_id}`);
        }
        setIsOpen(false);
    };

    const getTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return "just now";
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    const getActionIcon = (action) => {
        const icons = {
            created: "fa-plus-circle",
            updated: "fa-edit",
            deleted: "fa-trash",
            uploaded: "fa-upload",
            invited: "fa-user-plus",
            joined: "fa-user-check",
            left: "fa-user-minus",
            assigned: "fa-tasks",
            unassigned: "fa-user-times",
            status_changed: "fa-exchange-alt",
        };
        return icons[action] || "fa-bell";
    };

    return (
        <div className="notification-dropdown-container" ref={dropdownRef}>
            <button
                className="btn btn-outline-primary position-relative notification-bell"
                onClick={handleToggle}
                title="Notifications"
            >
                <i className="fa fa-bell"></i>
                {unreadCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger notification-badge">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h6 className="mb-0">Notifications</h6>
                        {unreadCount > 0 && (
                            <button
                                className="btn btn-link btn-sm p-0"
                                onClick={handleMarkAllAsRead}
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <div className="notification-list">
                        {loading ? (
                            <div className="notification-empty">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <i className="fa fa-bell-slash"></i>
                                <p>No notifications yet</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.read ? "unread" : ""}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon">
                                        <i className={`fa ${getActionIcon(notification.action)}`}></i>
                                    </div>
                                    <div className="notification-content">
                                        <p className="notification-text">
                                            <strong>{notification.user?.username}</strong>{" "}
                                            {notification.description}
                                        </p>
                                        <div className="notification-meta">
                                            {notification.project_name && (
                                                <span className="notification-project">
                                                    <i className="fa fa-folder me-1"></i>
                                                    {notification.project_name}
                                                </span>
                                            )}
                                            <span className="notification-time">
                                                {getTimeAgo(notification.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    {!notification.read && (
                                        <span className="notification-unread-dot"></span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="notification-footer">
                            <button
                                className="btn btn-link btn-sm"
                                onClick={() => {
                                    navigate("/notifications");
                                    setIsOpen(false);
                                }}
                            >
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
